import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, FileDown, LineChart as LineChartIcon, FileText } from "lucide-react";
import { StatusBadge, MACHINE_STATUSES, ReminderBadge, ExpiryBadge } from "@/components/Badges";
import { Attachments } from "@/components/Attachments";
import { DatePicker } from "@/components/DatePicker";
import { exportToCsv } from "@/lib/exportCsv";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfHeader, trAscii } from "@/lib/pdf";
import { toast } from "sonner";

const EMPTY = { code: "", plate: "", name: "", type: "", category: "İş Makinesi", location: "", operator: "", status: "Boşta-Atıl", reading_unit: "saat", current_reading: 0, service_interval: 0, last_service_reading: 0, warn_threshold: 0, next_service_date: "", inspection_date: "", insurance_date: "", kasko_date: "", exhaust_date: "" };

const CATEGORIES = ["Araç", "İş Makinesi", "Ekipman", "Diğer"];

const DATE_FIELDS = [
  { key: "inspection_date", label: "Muayene / Fenni" },
  { key: "insurance_date", label: "Trafik Sigortası" },
  { key: "kasko_date", label: "Kasko" },
  { key: "exhaust_date", label: "Egzoz Ölçümü" },
];

export default function Machines() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [histMachine, setHistMachine] = useState(null);
  const [readings, setReadings] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [newReading, setNewReading] = useState("");
  const [newNote, setNewNote] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const openDetail = async (m) => {
    setDetail(null); setDetailOpen(true);
    try {
      const res = await api.get(`/machines/${m.id}/detail`);
      setDetail(res.data);
    } catch (e) { toast.error("Detay yüklenemedi"); }
  };

  const downloadDetailPdf = () => {
    if (!detail) return;
    const m = detail.machine;
    const tr = trAscii;
    const doc = new jsPDF();
    let y = pdfHeader(doc);
    doc.setFontSize(15); doc.text(tr("Makine Detay Raporu"), 14, y); y += 9;
    doc.setFontSize(11); doc.setTextColor(90);
    doc.text(tr(`${m.name} (${m.code})  |  Plaka: ${m.plate || "-"}`), 14, y); y += 7;
    doc.text(tr(`Lokasyon: ${m.location}  |  Operator: ${m.operator || "-"}`), 14, y); y += 7;
    doc.text(tr(`Durum: ${m.status}  |  Sayac: ${(m.current_reading || 0)} ${m.reading_unit}`), 14, y); y += 9;
    doc.setTextColor(180, 30, 30);
    doc.text(tr(`Toplam Harcama: ${detail.total_cost.toLocaleString("tr-TR")} TL   |   Toplam Islem: ${detail.count}`), 14, y); y += 4;
    doc.setTextColor(0);
    autoTable(doc, {
      startY: y + 4,
      head: [["Tarih", "Tip", "Islem", "Degisen Parca", "Maliyet (TL)"].map(tr)],
      body: detail.maintenance.map((l) => [tr(l.date), tr(l.operation_type), tr(l.work_done), tr(l.changed_parts || "-"), tr((l.cost || 0).toLocaleString("tr-TR"))]),
      styles: { fontSize: 9 }, headStyles: { fillColor: [30, 58, 138] },
    });
    doc.save(`makine_raporu_${m.code}.pdf`);
    toast.success("PDF rapor indirildi");
  };

  const load = async (searchQ, cat) => {
    setLoading(true);
    try {
      const category = (cat ?? catFilter);
      const res = await api.get("/machines", { params: { q: (searchQ ?? q) || undefined, category: category !== "all" ? category : undefined } });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(() => load(q, catFilter), 300); return () => clearTimeout(t); }, [q, catFilter]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ code: r.code, plate: r.plate, name: r.name, type: r.type, category: r.category || "İş Makinesi", location: r.location, operator: r.operator, status: r.status, reading_unit: r.reading_unit || "saat", current_reading: r.current_reading || 0, service_interval: r.service_interval || 0, last_service_reading: r.last_service_reading || 0, warn_threshold: r.warn_threshold || 0, next_service_date: r.next_service_date || "", inspection_date: r.inspection_date || "", insurance_date: r.insurance_date || "", kasko_date: r.kasko_date || "", exhaust_date: r.exhaust_date || "" });
    setEditId(r.id); setOpen(true);
  };

  const num = (v) => parseFloat(v) || 0;
  const save = async () => {
    if (!form.code || !form.name || !form.location) {
      toast.error("Kod, Ad ve Lokasyon zorunludur");
      return;
    }
    const payload = { ...form, current_reading: num(form.current_reading), service_interval: num(form.service_interval), last_service_reading: num(form.last_service_reading), warn_threshold: num(form.warn_threshold) };
    try {
      if (editId) {
        await api.put(`/machines/${editId}`, payload);
        toast.success("Makine güncellendi");
      } else {
        await api.post("/machines", payload);
        toast.success("Makine eklendi");
      }
      setOpen(false);
      load(q);
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const remove = async (id) => {
    await api.delete(`/machines/${id}`);
    toast.success("Silindi");
    load(q);
  };

  const openHistory = async (m) => {
    setHistMachine(m); setHistOpen(true); setReadings([]); setNewReading(""); setNewNote(""); setHistLoading(true);
    try {
      const res = await api.get(`/machines/${m.id}/readings`);
      setReadings(res.data);
    } finally { setHistLoading(false); }
  };

  const addReading = async () => {
    const val = parseFloat(newReading);
    if (isNaN(val)) { toast.error("Geçerli bir sayaç değeri girin"); return; }
    try {
      await api.post(`/machines/${histMachine.id}/readings`, { reading: val, note: newNote });
      toast.success("Sayaç kaydedildi");
      const res = await api.get(`/machines/${histMachine.id}/readings`);
      setReadings(res.data);
      setNewReading(""); setNewNote("");
      load(q);
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const chartData = readings.map((r) => ({
    date: new Date(r.timestamp).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" }),
    reading: r.reading,
  }));

  const downloadChartPng = () => {
    const container = document.getElementById("reading-chart");
    const svg = container?.querySelector(".recharts-surface") || container?.querySelector("svg");
    if (!svg) { toast.error("Grafik bulunamadı"); return; }
    const clone = svg.cloneNode(true);
    const w = parseInt(svg.getAttribute("width")) || svg.clientWidth || 600;
    const h = parseInt(svg.getAttribute("height")) || svg.clientHeight || 260;
    clone.setAttribute("width", w); clone.setAttribute("height", h);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const svgStr = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = w * scale; canvas.height = h * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `sayac_gecmisi_${histMachine?.code || "makine"}.png`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        toast.success("Grafik PNG olarak indirildi");
      }, "image/png");
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast.error("Grafik dışa aktarılamadı"); };
    img.src = url;
  };

  const exportCsv = () => {
    if (rows.length === 0) { toast.info("Aktarılacak veri yok"); return; }
    exportToCsv("makine_arac", [
      { key: "code", label: "Kod" },
      { key: "plate", label: "Plaka" },
      { key: "name", label: "Makine Adı" },
      { key: "category", label: "Kategori" },
      { key: "type", label: "Tip" },
      { key: "location", label: "Lokasyon / Saha" },
      { key: "operator", label: "Sorumlu Operatör" },
      { key: "status", label: "Çalışma Durumu" },
      { key: "last_update", label: "Son Güncelleme", format: (v) => (v ? new Date(v).toLocaleString("tr-TR") : "") },
    ], rows);
  };

  const filtered = rows;

  return (
    <Layout
      title="Makine, Araç & Ekipman"
      subtitle="Araç, iş makinesi ve ekipmanların lokasyon, durum ve bakım takibi"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="export-machines-btn" onClick={exportCsv} variant="outline" className="rounded-sm gap-2">
            <FileDown className="w-4 h-4" /> Excel'e Aktar
          </Button>
          {canEdit && (
          <Button data-testid="add-machine-btn" onClick={openNew} className="rounded-sm gap-2 bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Kayıt Ekle
          </Button>
          )}
        </div>
      }
    >
      <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative w-full sm:max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="machine-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kod, plaka, saha ara..." className="pl-9 rounded-sm bg-white" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="machine-category-filter" className="rounded-sm bg-white w-full sm:w-52"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">Kod / Plaka</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Makine Adı</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Tip</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Lokasyon / Saha</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Operatör</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Durum</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Sonraki Bakım</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Belge / Muayene</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`machine-row-${r.code}`} className="odd:bg-white even:bg-zinc-50/50">
                <TableCell className="font-mono font-semibold">{r.code}<div className="text-xs text-muted-foreground font-normal">{r.plate || "-"}</div></TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground">
                  <span data-testid={`machine-category-${r.code}`} className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${r.category === "Ekipman" ? "bg-secondary/10 text-secondary border border-secondary/20" : r.category === "Araç" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-zinc-100 text-zinc-600 border border-zinc-200"}`}>{r.category || "İş Makinesi"}</span>
                  <div className="text-xs mt-0.5">{r.type || "-"}</div>
                </TableCell>
                <TableCell>{r.location}</TableCell>
                <TableCell>{r.operator || "-"}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell><ReminderBadge status={r.reminder_status} remaining={r.remaining} unit={r.reading_unit} /></TableCell>
                <TableCell><ExpiryBadge expiries={r.expiries} /></TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`detail-machine-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" onClick={() => openDetail(r)} title="Makine Detayı"><FileText className="w-4 h-4" /></Button>
                  <Button data-testid={`history-machine-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" onClick={() => openHistory(r)} title="Sayaç Geçmişi"><LineChartIcon className="w-4 h-4" /></Button>
                  {canEdit && <>
                  <Button data-testid={`edit-machine-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-machine-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Makine silinsin mi?</AlertDialogTitle>
                        <AlertDialogDescription>{r.name} ({r.code}) kalıcı olarak silinecek.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(r.id)}>Sil</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Makine Düzenle" : "Yeni Makine"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Makine/Araç Kodu *"><Input data-testid="field-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Plaka"><Input data-testid="field-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Makine Adı *"><Input data-testid="field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Kategori *">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="field-category" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Tip"><Input data-testid="field-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Örn. Matkap, Ekskavatör, Kamyon" className="rounded-sm" /></Field>
            <Field label="Lokasyon / Saha *"><Input data-testid="field-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Sorumlu Operatör"><Input data-testid="field-operator" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Çalışma Durumu">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="field-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{MACHINE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="col-span-2 border-t border-border pt-3 mt-1">
              <p className="text-xs font-bold uppercase tracking-wide text-primary">Bakım Takibi (Hatırlatıcı)</p>
              <p className="text-xs text-muted-foreground mt-0.5">Çalışma saati / KM sayacına ya da tarihe göre hatırlatıcı kurun. Takip istemiyorsanız aralık/tarih boş bırakın.</p>
            </div>
            <Field label="Bakım Türü">
              <Select value={form.reading_unit} onValueChange={(v) => setForm({ ...form, reading_unit: v })}>
                <SelectTrigger data-testid="field-reading-unit" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="saat">Çalışma Saati</SelectItem>
                  <SelectItem value="km">Kilometre (KM)</SelectItem>
                  <SelectItem value="gün">Tarih (Gün Bazlı)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.reading_unit === "gün" ? (
              <>
                <Field label="Sonraki Bakım Tarihi">
                  <DatePicker testid="field-next-service-date" value={form.next_service_date} onChange={(v) => setForm({ ...form, next_service_date: v })} clearable placeholder="Tarih seçin" />
                </Field>
                <Field label="Bakım Aralığı (gün, ops.)"><Input data-testid="field-service-interval" type="number" value={form.service_interval} onChange={(e) => setForm({ ...form, service_interval: e.target.value })} placeholder="örn. 180" className="rounded-sm" /></Field>
                <div className="col-span-2">
                  <Field label="Uyarı Eşiği (gün, ops.)"><Input data-testid="field-warn-threshold" type="number" value={form.warn_threshold} onChange={(e) => setForm({ ...form, warn_threshold: e.target.value })} placeholder="Boş = 15 gün önceden uyar" className="rounded-sm" /></Field>
                  <p className="text-xs text-muted-foreground mt-1">Bakım kaydı girdiğinizde, aralık (gün) doluysa sonraki bakım tarihi otomatik ileri atılır. Boşsa tarihi elle güncellersiniz.</p>
                </div>
              </>
            ) : (
              <>
                <Field label="Güncel Sayaç (saat/km)"><Input data-testid="field-current-reading" type="number" value={form.current_reading} onChange={(e) => setForm({ ...form, current_reading: e.target.value })} className="rounded-sm" /></Field>
                <Field label="Bakım Aralığı"><Input data-testid="field-service-interval" type="number" value={form.service_interval} onChange={(e) => setForm({ ...form, service_interval: e.target.value })} placeholder="örn. 500" className="rounded-sm" /></Field>
                <Field label="Son Bakım Sayacı"><Input data-testid="field-last-service" type="number" value={form.last_service_reading} onChange={(e) => setForm({ ...form, last_service_reading: e.target.value })} className="rounded-sm" /></Field>
                <Field label="Uyarı Eşiği (opsiyonel)"><Input data-testid="field-warn-threshold" type="number" value={form.warn_threshold} onChange={(e) => setForm({ ...form, warn_threshold: e.target.value })} placeholder="Boş = aralığın %10'u" className="rounded-sm" /></Field>
              </>
            )}
            <div className="col-span-2 border-t border-border pt-3 mt-1">
              <p className="text-xs font-bold uppercase tracking-wide text-primary">Tarih Bazlı Takip (Belge / Muayene)</p>
              <p className="text-xs text-muted-foreground mt-0.5">Bitiş tarihine 30 günden az kalınca sarı, süre dolunca kırmızı uyarı verilir. Boş bırakılan alan takip edilmez.</p>
            </div>
            {DATE_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <DatePicker testid={`field-${f.key.replace("_", "-")}`} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} clearable placeholder="Tarih seçin" />
              </Field>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-machine-btn" className="rounded-sm bg-primary hover:bg-primary/90" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <LineChartIcon className="w-5 h-5 text-secondary" />
              Sayaç Geçmişi — {histMachine?.name} ({histMachine?.code})
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {histLoading ? (
              <p className="text-sm text-muted-foreground py-10 text-center">Yükleniyor...</p>
            ) : chartData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">Henüz sayaç kaydı yok. Aşağıdan ilk değeri ekleyin.</p>
            ) : (
              <div id="reading-chart" className="bg-zinc-50 rounded-sm p-4 border border-border">
                <div className="flex justify-end mb-2">
                  <Button data-testid="download-chart-btn" onClick={downloadChartPng} variant="outline" size="sm" className="rounded-sm gap-2">
                    <FileDown className="w-3.5 h-3.5" /> PNG İndir
                  </Button>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} width={60} />
                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} formatter={(v) => [`${v.toLocaleString("tr-TR")} ${histMachine?.reading_unit || ""}`, "Sayaç"]} />
                    <Line type="monotone" dataKey="reading" stroke="#F97316" strokeWidth={2.5} dot={{ r: 3, fill: "#F97316" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {canEdit && (
            <div className="mt-4 border-t border-border pt-4">
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Yeni Sayaç Değeri Ekle ({histMachine?.reading_unit || "saat"})</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="new-reading-input" type="number" value={newReading} onChange={(e) => setNewReading(e.target.value)} placeholder="Güncel sayaç" className="rounded-sm" />
                <Input data-testid="new-reading-note" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Not (opsiyonel)" className="rounded-sm" />
                <Button data-testid="add-reading-btn" onClick={addReading} className="rounded-sm bg-primary hover:bg-primary/90 shrink-0">Kaydet</Button>
              </div>
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileText className="w-5 h-5 text-secondary" />
              Makine Detayı {detail ? `— ${detail.machine.name} (${detail.machine.code})` : ""}
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Yükleniyor...</p>
          ) : (
            <div className="py-2 space-y-4">
              <div className="flex justify-end">
                <Button data-testid="detail-pdf-btn" onClick={downloadDetailPdf} variant="outline" size="sm" className="rounded-sm gap-2">
                  <FileDown className="w-3.5 h-3.5" /> PDF Rapor İndir
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground">Lokasyon</p>
                  <p className="text-sm font-semibold mt-1">{detail.machine.location}</p>
                </div>
                <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground">Toplam İşlem</p>
                  <p className="font-mono text-2xl font-bold mt-1">{detail.count}</p>
                </div>
                <div className="bg-red-50 rounded-sm p-3 border border-red-200">
                  <p className="text-[10px] font-bold uppercase text-red-700">Toplam Harcama</p>
                  <p className="font-mono text-2xl font-bold mt-1 text-red-700">{detail.total_cost.toLocaleString("tr-TR")} ₺</p>
                </div>
                <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground">Sayaç</p>
                  <p className="font-mono text-sm font-bold mt-1">{(detail.machine.current_reading || 0).toLocaleString("tr-TR")} {detail.machine.reading_unit}</p>
                </div>
              </div>

              {Object.keys(detail.by_type).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(detail.by_type).map(([t, c]) => (
                    <span key={t} className="text-xs bg-zinc-100 border border-border rounded-sm px-2.5 py-1">
                      {t}: <strong className="font-mono">{c.toLocaleString("tr-TR")} ₺</strong>
                    </span>
                  ))}
                </div>
              )}

              {detail.total_cost >= 15000 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2 text-sm">
                  <span>⚠️</span>
                  <span>Bu makineye toplam <strong>{detail.total_cost.toLocaleString("tr-TR")} ₺</strong> bakım/onarım harcanmış. Yenileme (satıp yeni alma) değerlendirmesi yapmak isteyebilirsiniz.</span>
                </div>
              )}

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Bakım & Onarım Geçmişi</p>
                <div className="border border-border rounded-sm overflow-hidden max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="text-xs font-bold uppercase">Tarih</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Tip</TableHead>
                        <TableHead className="text-xs font-bold uppercase">İşlem</TableHead>
                        <TableHead className="text-right text-xs font-bold uppercase">Maliyet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.maintenance.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Kayıt yok</TableCell></TableRow>}
                      {detail.maintenance.map((l) => (
                        <TableRow key={l.id} data-testid={`detail-mnt-${l.id}`} className="odd:bg-white even:bg-zinc-50/50">
                          <TableCell className="font-mono text-sm">{l.date ? l.date.split("-").reverse().join(".") : "-"}</TableCell>
                          <TableCell className="text-sm">{l.operation_type}</TableCell>
                          <TableCell className="text-sm">{l.work_done}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{(l.cost || 0).toLocaleString("tr-TR")} ₺</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <Attachments entityType="machine" entityId={detail.machine.id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
